import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home, SmokeAlarm } from '@home-guard/shared';

const CURRENT_YEAR = new Date().getFullYear();

const SUGGESTED_LOCATIONS = [
  'Primary bedroom', 'Guest bedroom', 'Hallway — upstairs', 'Hallway — downstairs',
  'Kitchen', 'Living room', 'Basement', 'Garage', 'Office',
];

const schema = z.object({
  location:     z.string().min(1, 'Location is required').max(100).trim(),
  alarm_type:   z.enum(['smoke_only','co_combo','heat_detector','smart','']).optional(),
  power_type:   z.enum(['battery','hardwired','hardwired_backup','']).optional(),
  brand:        z.string().max(100).trim().optional(),
  install_year: z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().int().min(1990).max(CURRENT_YEAR + 1).nullable()),
  last_tested:  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD').optional().or(z.literal('')),
  last_battery: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD').optional().or(z.literal('')),
  notes:        z.string().max(500).trim().optional(),
});
type FormData = z.infer<typeof schema>;

export function AddSmokeAlarm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const home = (homesData?.data ?? []).find(h => h.is_primary) ?? homesData?.data?.[0];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const location = watch('location');

  const mutation = useMutation({
    mutationFn: (data: FormData) => api.post<SmokeAlarm>(`/homes/${home!.id}/smoke-alarms`, {
      location:     data.location,
      alarm_type:   data.alarm_type || null,
      power_type:   data.power_type || null,
      brand:        data.brand || null,
      install_year: data.install_year,
      last_tested:  data.last_tested || null,
      last_battery: data.last_battery || null,
      notes:        data.notes || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smoke-alarms', home?.id] });
      navigate('/app/smoke-alarms');
    },
  });

  if (!home) return null;

  const onSubmit = (data: FormData) => mutation.mutate(data);

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 100 }}>
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 16 }}
        >
          ← Back
        </button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#fff' }}>Add Smoke Alarm</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Location */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
            Location *
          </label>
          <input
            {...register('location')}
            placeholder="e.g. Primary bedroom"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: errors.location ? '1.5px solid #ef4444' : '1.5px solid #d1d5db',
              fontSize: 15, boxSizing: 'border-box',
            }}
          />
          {errors.location && <p style={{ margin: '4px 0 0', color: '#ef4444', fontSize: 12 }}>{errors.location.message}</p>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {SUGGESTED_LOCATIONS.map(loc => (
              <button
                key={loc}
                type="button"
                onClick={() => setValue('location', loc)}
                style={{
                  padding: '5px 10px', borderRadius: 6, fontSize: 12,
                  border: location === loc ? '1.5px solid #1D4ED8' : '1.5px solid #d1d5db',
                  background: location === loc ? '#eff6ff' : '#f9fafb',
                  color: location === loc ? '#1D4ED8' : '#6b7280',
                  cursor: 'pointer',
                }}
              >
                {loc}
              </button>
            ))}
          </div>
        </div>

        {/* Type */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 8 }}>
            Alarm type
          </label>
          <select
            {...register('alarm_type')}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15 }}
          >
            <option value="">Select type…</option>
            <option value="smoke_only">Smoke only</option>
            <option value="co_combo">Smoke + CO combination</option>
            <option value="heat_detector">Heat detector</option>
            <option value="smart">Smart alarm (Nest, Kidde)</option>
          </select>
        </div>

        {/* Power type */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 8 }}>
            Power source
          </label>
          <select
            {...register('power_type')}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15 }}
          >
            <option value="">Select power source…</option>
            <option value="battery">Battery only</option>
            <option value="hardwired">Hardwired (no battery backup)</option>
            <option value="hardwired_backup">Hardwired with battery backup</option>
          </select>
        </div>

        {/* Brand + install year */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
              Brand (optional)
            </label>
            <input
              {...register('brand')}
              placeholder="e.g. Kidde, First Alert, Nest"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
              Install year (optional)
            </label>
            <input
              {...register('install_year')}
              type="number"
              min={1990}
              max={CURRENT_YEAR}
              placeholder={String(CURRENT_YEAR)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Last tested + battery */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
              Last tested date
            </label>
            <input
              {...register('last_tested')}
              type="date"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
              Battery last replaced
            </label>
            <input
              {...register('last_battery')}
              type="date"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {mutation.error instanceof Error && (
          <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#ef4444', fontSize: 14 }}>
            Failed to add alarm. Please try again.
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          style={{
            padding: '14px', background: '#1D4ED8', color: '#fff', border: 'none',
            borderRadius: 12, fontWeight: 600, fontSize: 16, cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting || mutation.isPending ? 'Adding…' : 'Add smoke alarm'}
        </button>
      </form>

      <NavBar />
    </div>
  );
}
